import { useContext } from "react";
import { ConfiguratorContext } from "../context";

/**
 * Hook para manejar datos del vehículo usado
 * Proporciona funciones para obtener y actualizar información del vehículo usado
 */
const useUsedVehicleData = () => {
    const [state, setState] = useContext(ConfiguratorContext);

    /**
     * Obtiene los datos del vehículo usado
     */
    const getUsedVehicleData = () => state.usado;

    /**
     * Actualiza los datos del vehículo usado
     */
    const updateUsedVehicleData = (data) => {
        setState((prevState) => ({
            ...prevState,
            usado: {
                ...prevState.usado,
                ...data,
            },
        }));
    };

    /**
     * Obtiene la patente del vehículo usado
     */
    const getUsedVehiclePatent = () => state.usado.patent;

    /**
     * Obtiene los detalles del vehículo usado
     */
    const getUsedVehicleDetails = () => state.usado.details;

    /**
     * Obtiene la versión seleccionada del vehículo usado
     */
    const getUsedVehicleSelectedVersion = () => state.usado.selectedVersion;

    /**
     * Obtiene el kilometraje del vehículo usado
     */
    const getUsedVehicleMileage = () => state.usado.mileage;

    /**
     * Obtiene el rango de precios del vehículo usado
     */
    const getUsedVehicleRange = () => state.usado.range;

    /**
     * Verifica si se realizó mantenimiento
     */
    const getUsedVehicleMaintenanceChecked = () => state.usado.checkedMaintenance;

    /**
     * Actualiza solo la patente del vehículo usado
     */
    const updateUsedVehiclePatent = (patent) => {
        updateUsedVehicleData({ patent });
    };

    /**
     * Actualiza los detalles del vehículo usado
     */
    const updateUsedVehicleDetails = (details) => {
        updateUsedVehicleData({ details });
    };

    /**
     * Actualiza la versión seleccionada del vehículo usado
     */
    const updateUsedVehicleSelectedVersion = (selectedVersion) => {
        updateUsedVehicleData({ selectedVersion });
    };

    /**
     * Actualiza el kilometraje del vehículo usado
     */
    const updateUsedVehicleMileage = (mileage) => {
        updateUsedVehicleData({ mileage });
    };

    /**
     * Actualiza el rango de precios del vehículo usado
     */
    const updateUsedVehicleRange = (range) => {
        updateUsedVehicleData({ range });
    };

    /**
     * Actualiza el estado de mantenimiento
     */
    const updateUsedVehicleMaintenanceChecked = (checkedMaintenance) => {
        updateUsedVehicleData({ checkedMaintenance });
    };

    /**
     * Limpia todos los datos del vehículo usado
     */
    const clearUsedVehicleData = () => {
        setState((prevState) => ({
            ...prevState,
            usado: {
                patent: "",
                details: null,
                selectedVersion: null,
                mileage: null,
                range: null,
                checkedMaintenance: false,
            },
        }));
    };

    /**
     * Verifica si hay datos del vehículo usado
     */
    const hasUsedVehicleData = () => {
        const usado = state.usado;
        return usado.details && usado.selectedVersion && usado.range;
    };

    /**
     * Obtiene el precio mínimo del rango
     */
    const getUsedVehicleMinPrice = () => {
        const range = getUsedVehicleRange();
        return range?.min || null;
    };

    /**
     * Obtiene el precio máximo del rango
     */
    const getUsedVehicleMaxPrice = () => {
        const range = getUsedVehicleRange();
        return range?.max || null;
    };

    /**
     * Obtiene un precio estimado (promedio del rango)
     */
    const getUsedVehicleEstimatedPrice = () => {
        const range = getUsedVehicleRange();
        if (range?.min && range?.max) {
            return (range.min + range.max) / 2;
        }
        return null;
    };

    return {
        // Getters
        getUsedVehicleData,
        getUsedVehiclePatent,
        getUsedVehicleDetails,
        getUsedVehicleSelectedVersion,
        getUsedVehicleMileage,
        getUsedVehicleRange,
        getUsedVehicleMaintenanceChecked,
        getUsedVehicleMinPrice,
        getUsedVehicleMaxPrice,
        getUsedVehicleEstimatedPrice,
        
        // Setters
        updateUsedVehicleData,
        updateUsedVehiclePatent,
        updateUsedVehicleDetails,
        updateUsedVehicleSelectedVersion,
        updateUsedVehicleMileage,
        updateUsedVehicleRange,
        updateUsedVehicleMaintenanceChecked,
        
        // Utilities
        clearUsedVehicleData,
        hasUsedVehicleData,
    };
};

export default useUsedVehicleData;